<script setup lang="ts">
import useUserSession from '/@src/composable/useUserSession'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import useArticles from '/@src/composable/content/useArticles'
import useApi from '/@src/composable/useApi'
import type {
  ArticleListRequestParam,
  PageRequestParam,
} from '/@src/models/api'
import {CommonContentKinds, ContentKind, ContentStatus, MessageRequestParam} from '/@src/models/api'
import dayjs from 'dayjs'
import useGetComments from '/@src/composable/message/useGetComments'
import useUserFollowers from '/@src/composable/user/useUserFollower'
import useSearchHotContent from '/@src/composable/search/useSearchHotContent'
import { WebPath } from '/@src/constants/path'
import useCommonStat from '/@src/composable/analytics/useCommonStat'
import useOrderAnalytics from '/@src/composable/analytics/useRecentOrdersChart'
import useGetUserListRecentChart from '/@src/composable/analytics/useGetUserListRecentChart'
import useContents from "/@src/composable/content/useContents";
import useRouteNavigator from "/@src/composable/useRouteNavigator";
import {timeFormat, toISOLocal} from "/@src/utils/date";
import useHomePreference from "/@src/composable/user/useHomePreference";
import {Content} from "/@src/models/graphApi";
import useMallStatisticsOrders from '/@src/composable/analytics/useMallStatisticsOrdersChart'
import {mallStatisticsSwitch} from "/@src/state/userPreferenceState";
import useFinanceContent from '../../../composable/content/useFinanceContent'
import { getWeek } from '/@src/utils/date'

const { t, locale } = useI18n()

const {selectedWeekContents,loading:weekDataLoading} = useFinanceContent({
  startTime: toISOLocal(new Date(dayjs().startOf('week').day(1))),
  endTime: toISOLocal(new Date(dayjs().endOf('week').day(5))),
})

const userSession = useUserSession()
const {getSlogan} = useHomePreference()
const api = useApi()
const myArticleParam: ArticleListRequestParam = {
  userId: userSession.user.id,
  limit: 15,
}
const {articleResponse,getArticleList} = useArticles()
const articleParam: ArticleListRequestParam = {
  limit: 5,
  status: ContentStatus.PUBLISHED,
}
const {queryContents,contentListResponse,contentsLoading} = useContents()
const {routeContentDetail} = useRouteNavigator()
const articles = useArticles()
const commentsParam: MessageRequestParam = {
  limit: 1,
}
const useComments = useGetComments(api)
const followerParam: PageRequestParam = {
  id: userSession.user.id,
  limit: 5,
}
const useFollowers = useUserFollowers(api, followerParam)
const useDashboardStat = useCommonStat(api)
const userOrderAnalyticsRepository = useOrderAnalytics()
const useGetUserRecentChart = useGetUserListRecentChart()
const useGetMallStatisticsOrders = useMallStatisticsOrders()
const isShowMallStatisticsSwitch = ref(false)

let slogan = getSlogan()
onMounted(() => {
  //获取今日热门
  useHotContent.queryTodayHotContent()
  getArticleList(myArticleParam)
  // 全部文章
  articles.getArticleList(articleParam)
  useComments.getCommentsList(commentsParam)
  useFollowers.getUserFollowers()
  useDashboardStat.getDashboardAnalytics()
  userOrderAnalyticsRepository.getRecentOrderDataList()
  useGetUserRecentChart.getUserChart()
  //获取商城的数据
  useGetMallStatisticsOrders.getMallStatisticsOrders()
  isShowMallStatisticsSwitch.value = mallStatisticsSwitch.value
})
const flowTimeFormat = (date, formatText) => {
  let format = formatText
  if (!formatText) {
    format = 'MMM DD'
  }
  return dayjs(new Date(date)).format(format)
}
const isArticleLoading = ref(true)
watch(articleResponse, () => {
  isArticleLoading.value = false
})
const isCommentLoading = ref(true)
watch(useComments.response, () => {
  isCommentLoading.value = false
})
const isFollowerLoading = ref(true)
watch(useFollowers.response, () => {
  isFollowerLoading.value = false
})
/**
 * 热门内容
 */
const useHotContent = useSearchHotContent()
// 是否显示最新内容
const isFlowLatestContent = ref(false)
/**
 * 更新最新文章
 * @param isLatest
 */
const updateLatestFlow = (isLatest:boolean) => {
  isFlowLatestContent.value = isLatest
  if (!isLatest && !useHotContent.hotContents) {
    useHotContent.queryTodayHotContent()
  } else if (isLatest && !articles.articleResponse.value) {
    articles.getArticleList({
      limit:8,
      status: ContentStatus.PUBLISHED
    })
  }
}
const revenueChart = ref(null)
const orderChart = ref(null)
const userChart = ref(null)
const orderTypesChart = ref(null)
const isShowNotificationDialog = ref(false)

/**
 * 根据content类型加载icon
 * @param item
 */
const loadContentTypeIcon = (item:Content):string=>{
  let res = '/images/icons/components/ic-content-article.svg'
  switch (item.kind){
    case ContentKind.ARTICLE:
      res = '/images/icons/components/ic-content-article.svg'
      break
    case ContentKind.VIDEO:
      res = '/images/icons/components/ic-content-video.svg'
      break
    case ContentKind.REPORT:
      res = '/images/icons/components/ic-content-report.svg'
      break
  }
  return res
}
/**
 * 监测语音变化，更新图表内等文字
 */
watch(locale, async () => {
  console.log(orderTypesChart)
  /** 收入图表 */
  userOrderAnalyticsRepository.salesChartOptions.value.title.text = t(
    'title-charts-recent-revenue'
  )
  userOrderAnalyticsRepository.salesChartOptions.value.series[0].name = t(
    'text-series-revenue'
  )
  userOrderAnalyticsRepository.salesChartOptions.value.chart.defaultLocale =
    locale.value.toLocaleLowerCase()
  /** 订单图表 */
  userOrderAnalyticsRepository.orderChartOptions.value.title.text = t(
    'title-charts-recent-order'
  )
  userOrderAnalyticsRepository.orderChartOptions.value.series[0].name =
    t('text-series-order')
  userOrderAnalyticsRepository.orderChartOptions.value.chart.defaultLocale =
    locale.value.toLocaleLowerCase()
  /** 用户图表 */
  useGetUserRecentChart.userChartOptions.value.title.text = t(
    'title-charts-new-user'
  )
  useGetUserRecentChart.userChartOptions.value.series[0].name =
    t('text-series-user')
  useGetUserRecentChart.userChartOptions.value.chart.defaultLocale =
    locale.value.toLocaleLowerCase()
  userOrderAnalyticsRepository.orderTypeChartOptions.value.title.text = t(
    'title-charts-recent-payments-type'
  )
  await nextTick()
  revenueChart.value.refresh()
  orderChart.value.refresh()
  userChart.value.refresh()
  orderTypesChart.value.refresh()
})

const revenueChangeRate = computed(() => {
  const thisWeek = userOrderAnalyticsRepository?.orderAnalyticsResponse?.value?.thisWeekRevenue
  const lastWeek = userOrderAnalyticsRepository?.orderAnalyticsResponse?.value?.lastWeekRevenue
  if (thisWeek > lastWeek) {
    return parseFloat((100 - (lastWeek * 100) / thisWeek).toFixed(2)) + '%'
  } else {
    return -parseFloat((100 - (thisWeek * 100) / lastWeek).toFixed(2)) + '%'
  }
})
const getWebArticlePath = (id:number) => {
  // let host = import.meta.env.VITE_WEB_HOST_URL + WebPath.ARTICLE_DETAIL
  // return host + id
  return '/content/article/' + id
}
/**
 * 处理点击推送
 */
const handleClickNotification = () => {
  isShowNotificationDialog.value = true
}
</script>
<template>
  <div class="dashboard-home">
    <div class="columns">
      <div class="column is-8">
        <div class="columns is-multiline">
          <!--Header-->
          <div class="column is-12">
            <div class="header-top">
              <div class="header-image">
                <img
                  src="/@src/assets/illustrations/dashboards/reading.svg"
                  alt=""
                />
              </div>
              <div class="header-meta">
                <h3>Hello, {{ userSession.user.nickname }}.</h3>
<!--                <p>-->
<!--                  Have any ideas for a new article? If not, you should-->
<!--                  definitely check the feed for some inspiration.-->
<!--                </p>-->
                <p>{{slogan}}</p>
                <div class="header-option">
                  <RouterLink :to="{ name: 'content-article-create' }">
                    <V-Button light outlined icon="feather:plus">
                      <span>{{ t('create-article') }}</span>
                    </V-Button>
                  </RouterLink>
<!--                  <RouterLink :to="{ name: 'content-video-create' }">-->
<!--                    <V-Button light outlined icon="feather:plus">-->
<!--                      {{ t('create-video') }}-->
<!--                    </V-Button>-->
<!--                  </RouterLink>-->
                  <RouterLink
                    :to="{ name: 'content-calendar'}">
                    <V-Button light outlined icon="feather:plus">
                      {{ t('create-calendar') }}
                    </V-Button>
                  </RouterLink>
                  <button
                    class="push-button button is-circle is-primary" @click="handleClickNotification">
                    <span class="icon is-small">
                      <i
                        aria-hidden="true"
                        class="iconify"
                        data-icon="feather:send"
                      ></i>
                    </span>
                        <span>{{t('title-notification-dialog')}}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!--Content-->
          <div class="column is-7">
            <div class="writing-stats">
              <!--Stat-->
              <div class="writing-stat">
                <span>{{ t('dashboard-articles-count') }}</span>
                <V-Loader size="small" :active="isArticleLoading">
                  <span class="stat dark-inverted">{{
                    articleResponse && articleResponse.count
                  }}</span>
                </V-Loader>
              </div>
              <!--Stat-->
              <div class="writing-stat">
                <span>{{ t('dashboard-comments-count') }}</span>
                <V-Loader size="small" :active="isCommentLoading">
                  <span class="stat dark-inverted">{{
                    useComments.response.value &&
                    useComments.response.value.total_count
                  }}</span>
                </V-Loader>
              </div>
              <!--Stat-->
              <div class="writing-stat">
                <!--                <span>{{ t('dashboard-follower-count') }}</span>-->
                <span v-t="'dashboard-follower-count'"></span>
                <V-Loader size="small" :active="isFollowerLoading">
                  <span class="stat dark-inverted">{{
                    useFollowers.response.value &&
                    useFollowers.response.value.total_count
                  }}</span>
                </V-Loader>
              </div>
            </div>

            <div class="featured-analytics">
              <!--Header-->
              <div class="featured-analytics-header">
                <h3 v-t="'title-recent-analytics-section'" class="dark-inverted"></h3>
                <a v-t="'text-view-all'" :href="'/finance/statistics'" class="action-link"></a>
              </div>
              <!--数据统计-->
              <div class="columns is-multiline">
                <!--收入图表-->
                <div class="column is-12" v-if="isShowMallStatisticsSwitch">
                  <apexchart
                    v-if="
                      useGetMallStatisticsOrders.mallSaleschartOptions.value.chart
                    "
                    id="revenue-chart"
                    ref="revenueChart"
                    :height="
                      useGetMallStatisticsOrders.mallSaleschartOptions.value.chart
                        .height
                    "
                    :type="
                      useGetMallStatisticsOrders.mallSaleschartOptions.value.chart
                        .type
                    "
                    :series="
                      useGetMallStatisticsOrders.mallSaleschartOptions.value
                        .series
                    "
                    :options="
                      useGetMallStatisticsOrders.mallSaleschartOptions.value
                    "
                  >
                  </apexchart>
                  <V-Loader v-else size="small" active>
                    <div style="height: 100px"></div>
                  </V-Loader>
                </div>
                <div class="column is-12" v-else>
                  <apexchart
                    v-if="
                      userOrderAnalyticsRepository.salesChartOptions.value.chart
                    "
                    id="revenue-chart"
                    ref="revenueChart"
                    :height="
                      userOrderAnalyticsRepository.salesChartOptions.value.chart
                        .height
                    "
                    :type="
                      userOrderAnalyticsRepository.salesChartOptions.value.chart
                        .type
                    "
                    :series="
                      userOrderAnalyticsRepository.salesChartOptions.value
                        .series
                    "
                    :options="
                      userOrderAnalyticsRepository.salesChartOptions.value
                    "
                  >
                  </apexchart>
                  <V-Loader v-else size="small" active>
                    <div style="height: 100px"></div>
                  </V-Loader>
                </div>
                <!--订单图表-->
                <div class="column is-12" v-if="isShowMallStatisticsSwitch">
                  <apexchart
                    v-if="
                      useGetMallStatisticsOrders.mallOrderChartOptions.value.chart
                    "
                    id="order-chart"
                    ref="orderChart"
                    :height="
                      useGetMallStatisticsOrders.mallOrderChartOptions.value.chart
                        .height
                    "
                    :type="
                      useGetMallStatisticsOrders.mallOrderChartOptions.value.chart
                        .type
                    "
                    :series="
                      useGetMallStatisticsOrders.mallOrderChartOptions.value
                        .series
                    "
                    :options="
                      useGetMallStatisticsOrders.mallOrderChartOptions.value
                    "
                  >
                  </apexchart>
                  <V-Loader v-else size="small" active>
                    <div style="height: 100px"></div>
                  </V-Loader>
                </div>
                <div class="column is-12" v-else>
                  <apexchart
                    v-if="
                      userOrderAnalyticsRepository.orderChartOptions.value.chart
                    "
                    id="order-chart"
                    ref="orderChart"
                    :height="
                      userOrderAnalyticsRepository.orderChartOptions.value.chart
                        .height
                    "
                    :type="
                      userOrderAnalyticsRepository.orderChartOptions.value.chart
                        .type
                    "
                    :series="
                      userOrderAnalyticsRepository.orderChartOptions.value
                        .series
                    "
                    :options="
                      userOrderAnalyticsRepository.orderChartOptions.value
                    "
                  >
                  </apexchart>
                  <V-Loader v-else size="small" active>
                    <div style="height: 100px"></div>
                  </V-Loader>
                </div>
                <!--收入产品分布-->
                <div class="column is-12" v-if="isShowMallStatisticsSwitch">
                  <apexchart
                    v-if="
                      useGetMallStatisticsOrders.mallOrderTypeChartOptions.value
                        .chart
                    "
                    id="order-type-chart"
                    ref="orderTypesChart"
                    :height="
                      useGetMallStatisticsOrders.mallOrderTypeChartOptions.value
                        .chart.height
                    "
                    :type="
                      useGetMallStatisticsOrders.mallOrderTypeChartOptions.value
                        .chart.type
                    "
                    :series="
                      useGetMallStatisticsOrders.mallOrderTypeChartOptions.value
                        .series
                    "
                    :options="
                      useGetMallStatisticsOrders.mallOrderTypeChartOptions.value
                    "
                  >
                  </apexchart>
                  <V-Loader v-else size="small" active>
                    <div style="height: 100px"></div>
                  </V-Loader>
                </div>
                <div class="column is-12" v-else>
                  <apexchart
                    v-if="
                      userOrderAnalyticsRepository.orderTypeChartOptions.value
                        .chart
                    "
                    id="order-type-chart"
                    ref="orderTypesChart"
                    :height="
                      userOrderAnalyticsRepository.orderTypeChartOptions.value
                        .chart.height
                    "
                    :type="
                      userOrderAnalyticsRepository.orderTypeChartOptions.value
                        .chart.type
                    "
                    :series="
                      userOrderAnalyticsRepository.orderTypeChartOptions.value
                        .series
                    "
                    :options="
                      userOrderAnalyticsRepository.orderTypeChartOptions.value
                    "
                  >
                  </apexchart>
                  <V-Loader v-else size="small" active>
                    <div style="height: 100px"></div>
                  </V-Loader>
                </div>
                <!--用户图表-->
                <div class="column is-12">
                  <apexchart
                    v-if="useGetUserRecentChart.userChartOptions.value.chart"
                    id="user-chart"
                    ref="userChart"
                    :height="
                      useGetUserRecentChart.userChartOptions.value.chart.height
                    "
                    :type="
                      useGetUserRecentChart.userChartOptions.value.chart.type
                    "
                    :series="
                      useGetUserRecentChart.userChartOptions.value.series
                    "
                    :options="useGetUserRecentChart.userChartOptions.value"
                  >
                  </apexchart>
                  <V-Loader v-else size="small" active>
                    <div style="height: 100px"></div>
                  </V-Loader>
                </div>
              </div>
            </div>
          </div>

          <div class="column is-5">
          <!-- 本周收入对比 -->
            <div
              v-if="userOrderAnalyticsRepository.orderAnalyticsResponse.value || useGetMallStatisticsOrders"
              class="dashboard-tile"
            >
              <div class="tile-head">
                <h3 class="dark-inverted">{{ t('revenue-this-week') }}</h3>
                <V-IconBox color="primary" size="small" rounded>
                  <i aria-hidden="true" class="fas fa-gem"></i>
                </V-IconBox>
              </div>
              <div class="tile-body">
                <span class="dark-inverted" v-if="isShowMallStatisticsSwitch">
                  ￥{{
                    useGetMallStatisticsOrders.thisWeek.value
                  }}
                </span>
                <span class="dark-inverted" v-else>
                  ￥{{
                    userOrderAnalyticsRepository?.orderAnalyticsResponse?.value?.thisWeekRevenue.toLocaleString()
                  }}
                </span>
              </div>
              <div class="tile-foot" v-if="isShowMallStatisticsSwitch">
                <span
                  v-if="
                    useGetMallStatisticsOrders.thisWeek.value >
                    useGetMallStatisticsOrders.lastWeek.value
                  "
                  class="text-h-green"
                >{{ useGetMallStatisticsOrders.weekRevenueRate.value }}
                  <i
                    aria-hidden="true"
                    class="iconify"
                    data-icon="feather:trending-up"
                  />
                </span>
                <span v-else class="text-h-red"
                >{{ useGetMallStatisticsOrders.weekRevenueRate.value }}
                  <i
                    aria-hidden="true"
                    class="iconify"
                    data-icon="feather:trending-down"
                  />
                </span>
                <span>{{ t('text-since-last-week') }}</span>
              </div>
              <div class="tile-foot" v-else>
                <span
                  v-if="
                    userOrderAnalyticsRepository?.orderAnalyticsResponse?.value
                      ?.thisWeekRevenue >
                    userOrderAnalyticsRepository?.orderAnalyticsResponse?.value?.lastWeekRevenue
                  "
                  class="text-h-green"
                  >{{ revenueChangeRate }}
                  <i
                    aria-hidden="true"
                    class="iconify"
                    data-icon="feather:trending-up"
                  />
                </span>
                <span v-else class="text-h-red"
                  >{{ revenueChangeRate }}
                  <i
                    aria-hidden="true"
                    class="iconify"
                    data-icon="feather:trending-down"
                  />
                </span>
                <span>{{ t('text-since-last-week') }}</span>
              </div>
            </div>
            <!-- 快捷热搜词配置 -->
            <hot-search-words/>
            <!--Updates-->
            <div class="updates">
              <!--Header-->
              <div class="updates-header">
                <h3 class="dark-inverted">{{ t('title-my-update') }}</h3>
                <a class="action-link" :href="'/content/article/list?userId='+userSession.user.id">{{ t('text-view-all') }}</a>
              </div>

              <div v-if="!contentsLoading&&articleResponse" class="updates-list">
                <!--Update-->
                <router-link v-for="(item, index) in articleResponse.list.slice(0,10)"
                             :to="routeContentDetail(ContentKind.ARTICLE,item.id)"
                             :key="index">
                  <div

                    class="update-item is-dark-bordered-12"
                  >
                      <p>{{ item.title }}</p>
                      <span class="dark-inverted">{{
                        flowTimeFormat(item.display_time)
                      }}</span>
                  </div>
                </router-link>
              </div>
              <div v-else>
                <div class="loading-update-item is-dark-bordered-12">
                  <V-PlaceloadText
                    :lines="2"
                    width="75%"
                    last-line-width="25%"
                  />
                </div>
                <div class="loading-update-item is-dark-bordered-12">
                  <V-PlaceloadText
                    :lines="2"
                    width="75%"
                    last-line-width="25%"
                  />
                </div>
                <div class="loading-update-item is-dark-bordered-12">
                  <V-PlaceloadText
                    :lines="2"
                    width="75%"
                    last-line-width="25%"
                  />
                </div>
                <div class="loading-update-item is-dark-bordered-12">
                  <V-PlaceloadText
                    :lines="2"
                    width="75%"
                    last-line-width="25%"
                  />
                </div>
                <div class="loading-update-item is-dark-bordered-12">
                  <V-PlaceloadText
                    :lines="2"
                    width="75%"
                    last-line-width="25%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    
      <!-- 右侧热度 -->
      <div class="column is-4">
        <!-- 右侧本周事件 -->
        <VCard class="event-card">
          <div class="event-preview">
            <div class="event-preview-top">
              <div class="event-preview-top-title">本周事件</div>
              <a v-t="'action-edit'" :href="'/content/events'" class="action-link"></a>
            </div>
            <WeekEventList :list="selectedWeekContents" :title="t('title-weeks-event')" :loading="weekDataLoading"></WeekEventList>
          </div>
        </VCard>
        <div class="articles-feed">
          <!--Header-->
          <div class="articles-feed-header">
            <h3 class="dark-inverted">{{ t('title-new-content') }}</h3>
            <a class="action-link" href="/content/article/list">{{ t('text-view-all-articles') }}</a>
          </div>
          <!--Subheader-->
          <div class="articles-feed-subheader">
            <div class="selector">
              <button
                :class="isFlowLatestContent ? 'button' : 'is-selected button'"
                @click="updateLatestFlow(false)"
              >
                {{ t('dashboard-article-option-today-popular') }}
              </button>
              <button
                :class="isFlowLatestContent ? 'is-selected button' : 'button'"
                @click="updateLatestFlow(true)"
              >
                {{ t('text-latest-articles') }}
              </button>
            </div>
          </div>
          <!--List-->
          <div class="articles-feed-list">
            <div class="articles-feed-list-inner">
              <!--Item-->
              <div v-if="isFlowLatestContent && articles.articleResponse.value">
                <a
                  v-for="(item, index) in articles.articleResponse.value.list.slice(0,5)"
                  :key="index"
                  class="articles-feed-item"
                  target="_blank"
                  :href="getWebArticlePath(item.id)"
                >
                  <div class="featured-image">
                    <img
                      :src="item.cover_uri || '/images/img-loading-default.png'"
                      alt=""
                      @error.once="
                        $event.target.src = '/images/img-loading-default.png'
                      "
                    />
                  </div>
                  <div class="featured-content">
                    <h4 class="dark-inverted">{{ item.title }}</h4>

                    <V-Block
                      :title="item.author?.nickname||''"
                      :subtitle="
                        t('title-update-at') +
                        ': ' +
                        flowTimeFormat(item.display_time, 'YYYY/MM/DD')
                      "
                      narrow
                    >
                      <template #icon>
                        <V-Avatar :picture="item.author.avatar" squared />
                      </template>
                    </V-Block>
                  </div>
                </a>
              </div>
              <!-- 热门文章 -->
              <div
                v-else-if="
                  !isFlowLatestContent &&
                  useHotContent.hotContents.value
                "
              >
                <a
                  v-for="(item, index) in useHotContent.hotContents.value.slice(0,5)"
                  :key="index"
                  class="articles-feed-item"
                  target="_blank"
                  :href="routeContentDetail(item.kind,item.raw_id,item.title)"
                >
                  <div class="featured-image">
                    <img
                      v-lazy="item.cover_url"
                      alt=""
                      @error.once="
                        $event.target.src = '/images/img-loading-default.png'
                      "
                    />
                    <img class="content-type" style="padding-right: 4px" :src="loadContentTypeIcon(item)"/>
                  </div>
                  <div class="featured-content">
                    <h4 class="dark-inverted">{{ item.title }}</h4>
                    <V-Block
                      :title="item.author&&item.author.nickname||' '"
                      :subtitle="timeFormat(item.datetime,'MMM DD,YYYY,HH:mm')"
                      :infratitle="item.counter.viewed+' '+ t('viewed-total-in-history')"
                      center
                    >
                      <template #icon>
                        <V-Avatar :picture="item.author&&item.author.avatar||''" squared />
                      </template>
                    </V-Block>
                  </div>
                </a>
              </div>
              <div v-else>
                <a class="articles-feed-item">
                  <div class="featured-image">
                    <img
                      src="/images/img-loading-default.png"
                      alt=""
                      @error.once="
                        $event.target.src =
                          '/images/img-loading-default.png'
                      "
                    />
                  </div>
                  <div class="featured-content">
                    <h4 class="dark-inverted">
                      <V-PlaceloadText :lines="1" />
                    </h4>
                    <p>
                      <V-PlaceloadText
                        :lines="2"
                        width="75%"
                        last-line-width="25%"
                      />
                    </p>

                    <V-Block :title="''" subtitle="''" narrow>
                      <template #icon>
                        <V-PlaceloadAvatar />
                      </template>
                    </V-Block>
                  </div>
                </a>
                <a class="articles-feed-item">
                  <div class="featured-image">
                    <img
                      src="/images/img-loading-default.png"
                      alt=""
                      @error.once="
                        $event.target.src =
                          '/images/img-loading-default.png'
                      "
                    />
                  </div>
                  <div class="featured-content">
                    <h4 class="dark-inverted">
                      <V-PlaceloadText :lines="1" />
                    </h4>
                    <p>
                      <V-PlaceloadText
                        :lines="2"
                        width="75%"
                        last-line-width="25%"
                      />
                    </p>

                    <V-Block :title="''" subtitle="''" narrow>
                      <template #icon>
                        <V-PlaceloadAvatar />
                      </template>
                    </V-Block>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <notification-dialog
      ref="notificationDialog"
      :show-type-option="true"
      v-model:open="isShowNotificationDialog"
    />
  </div>
</template>
<style lang="scss">
//@import '../../../scss/abstracts/_variables.scss';
@import '../../../scss/abstracts/_mixins.scss';
.dashboard-home {
  .push-button{
    padding: .7rem;
    background: white;
    color: var(--dark-text);
  }
  .push-button:hover{
    border-style: solid;
    border-color: white;
  }
  .dummy{
    display: flex;
  }
  .header-top {
    display: flex;
    align-items: center;
    padding: 10px;
    border-radius: 16px;
    background: var(--primary);
    font-family: var(--font);
    box-shadow: var(--primary-box-shadow);
    .header-image {
      position: relative;
      height: 175px;
      width: 320px;

      img {
        position: absolute;
        top: 0;
        left: -40px;
        display: block;
        pointer-events: none;
      }
    }

    .header-meta {
      margin-left: 0;
      padding-right: 30px;
      .header-option {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }
      h3 {
        color: var(--smoke-white);
        font-family: var(--font-alt);
        font-weight: 700;
        font-size: 1.3rem;
        max-width: 280px;
      }

      p {
        font-weight: 400;
        color: var(--smoke-white-dark-2);
        margin-bottom: 16px;
        max-width: 320px;
      }

      .action-link {
        span {
          font-size: 0.8rem;
          text-transform: uppercase;
          margin-right: 6px;
        }

        i {
          font-size: 12px;
        }
      }
    }
  }

  .writing-stats {
    display: flex;
    margin-bottom: 1rem;
    margin-left: -8px;
    margin-right: -8px;

    .writing-stat {
      @include vuero-l-card();

      margin: 8px;
      width: calc(33.3% - 16px);
      padding: 12px;

      span {
        display: block;
        font-family: var(--font-alt);
        font-size: 0.8rem;
        font-weight: 500;
        text-transform: uppercase;
        margin-bottom: 5px;
        color: var(--light-text);
      }
      .stat {
        min-height: 30px;
        font-family: var(--font);
        font-weight: 700;
        font-size: 1.8rem;
        color: var(--dark-text);
      }
    }
  }

  .featured-analytics {
    @include vuero-l-card();

    padding: 20px;

    .featured-analytics-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;

      h3 {
        font-family: var(--font-alt);
        font-weight: 600;
        font-size: 1.1rem;
        color: var(--dark-text);
      }

      .action-link {
        font-size: 0.9rem;
      }
    }
    .analytics-section {
      .analytics-charts-item {
        &:not(:last-child) {
          margin-bottom: 20px;
        }
      }
    }
    .featured-authors-list {
      .featured-authors-item {
        &:not(:last-child) {
          margin-bottom: 20px;
        }

        .media-flex-center {
          .flex-end {
            span {
              font-family: var(--font-alt);
              font-weight: 600;
              color: var(--dark-text);
            }
          }
        }
      }
    }
  }
  .dashboard-tile {
    @include vuero-s-card();

    font-family: var(--font);
    .text-h-green {
      color: var(--green);
    }

    .text-h-red {
      color: var(--red);
    }
    .tile-head {
      display: flex;
      align-items: center;
      justify-content: space-between;

      h3 {
        font-family: var(--font-alt);
        color: var(--dark-text);
        font-weight: 600;
      }
    }

    .tile-body {
      font-size: 2rem;
      padding: 4px 0 8px 0;

      span {
        color: var(--dark-text);
        font-weight: 600;
      }
    }

    .tile-foot {
      span {
        &:first-child {
          font-weight: 500;

          svg {
            height: 16px;
            width: 16px;
            margin-right: 6px;
            stroke-width: 3px;
          }
        }

        &:nth-child(2) {
          color: var(--light-text);
          font-size: 0.9rem;
        }
      }
    }
  }
  .hot-search-words {
    margin-top: 8px;
  }
  .updates {
    @include vuero-l-card();

    padding: 20px;
    margin-top: 8px;

    .updates-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;

      h3 {
        font-family: var(--font-alt);
        font-weight: 600;
        font-size: 1.1rem;
        color: var(--dark-text);
      }

      .action-link {
        font-size: 0.9rem;
      }
    }

    .updates-list {
      .update-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid var(--fade-grey-dark-3);

        &:last-child {
          margin-bottom: 0;
          border-bottom: none;
        }

        p {
          font-size: 0.9rem;
          @include textLine(2);
        }

        span {
          display: block;
          min-width: 60px;
          text-align: right;
          font-family: var(--font);
          font-weight: 600;
          font-size: 0.8rem;
          color: var(--dark-text);
        }
      }
    }
    .loading-update-item {
      margin-bottom: 16px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--fade-grey-dark-3);
      &:last-child {
        margin-bottom: 0;
        border-bottom: none;
      }
    }
  }
  .event-card {
    margin-bottom: 10px;
    .event-preview {
      display: flex;
      justify-content: center;
      flex-direction: column;
      &-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;
        &-title {
          font-family: var(--font-alt);
          color: var(--dark-text);
          font-weight: 600;
        }
      }
    }
  }

  .articles-feed {
    background: var(--widget-grey);
    padding: 30px;
    border-radius: 12px;

    .articles-feed-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;

      h3 {
        font-family: var(--font-alt);
        font-weight: 600;
        font-size: 1.1rem;
        color: var(--dark-text);
      }

      .action-link {
        font-size: 0.9rem;
      }
    }

    .articles-feed-subheader {
      margin-bottom: 20px;

      .selector {
        .button {
          font-size: 0.8rem;
          border-radius: 50px;
          margin-right: 4px;

          &.is-selected {
            background: var(--primary);
            color: var(--white);
            border-color: var(--primary);
            box-shadow: var(--primary-box-shadow);
          }
        }
      }
    }

    .articles-feed-list {
      .articles-feed-list-inner {
        .articles-feed-item {
          display: block;

          &:not(:last-child) {
            margin-bottom: 20px;
          }

          .featured-image {
            position: relative;
            height: 150px;
            overflow: hidden;
            border-top-left-radius: 18px;
            border-top-right-radius: 18px;

            img {
              display: block;
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .content-type{
              position: absolute;
              top: 10px;
              right: 10px;
              width: unset;
              height: unset;
            }
          }

          .featured-content {
            position: relative;
            padding: 25px;
            border-radius: 18px;
            background: var(--white);
            margin-top: -40px;
            z-index: 1;

            h4,
            p {
              margin-bottom: 10px;
            }

            h4 {
              font-family: var(--font-alt);
              font-size: 1rem;
              font-weight: 600;
              color: var(--dark-text);
            }

            .media-flex-center {
              .flex-meta {
                span {
                  font-size: 0.8rem;
                }
              }
            }
          }
        }
      }
    }
  }
}

.is-dark {
  .dashboard-home {
    .push-button{
      color: var(--white);
    }
    .header-top {
      background: var(--dark-sidebar);
      box-shadow: none;
    }

    .writing-stats {
      .writing-stat {
        @include vuero-card--dark();
      }
    }

    .featured-analytics,
    analytics-card {
      @include vuero-card--dark();
    }
    .dashboard-tile,
    .dashboard-card {
      @include vuero-card--dark();
    }

    .updates,
    .featured-authors {
      @include vuero-card--dark();
    }

    .articles-feed {
      background: var(--dark-sidebar-light-8);

      .articles-feed-subheader {
        .selector {
          .button {
            &.is-selected {
              background: var(--primary) !important;
              border-color: var(--primary) !important;
              box-shadow: var(--primary-box-shadow) !important;
              color: var(--white) !important;
            }
          }
        }
      }

      .articles-feed-list {
        .articles-feed-list-inner {
          .articles-feed-item {
            .featured-content {
              background: var(--dark-sidebar);
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 767px) {
  .dashboard-home {
    .header-top {
      flex-direction: column;
      text-align: center;

      .header-image {
        height: auto;
        width: 100%;

        img {
          position: relative;
          width: 100%;
          max-width: 260px;
          margin: 0 auto;
          top: 0;
          left: 0;
          margin-top: -34px;
        }
      }

      .header-meta {
        padding: 20px;

        > p {
          max-width: 280px;
          margin-left: auto;
          margin-right: auto;
        }
      }
    }

    .writing-stats {
      .writing-stat {
        text-align: center;
      }
    }
  }
}

@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
  .dashboard-home {
    .articles-feed {
      .articles-feed-list {
        .articles-feed-list-inner {
          display: flex;
          flex-wrap: wrap;
          margin-left: -12px;
          margin-right: -12px;

          .articles-feed-item {
            width: calc(50% - 24px);
            margin: 12px;
          }
        }
      }
    }
  }
}

@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  .dashboard-home {
    .updates {
      .updates-list {
        .update-item {
          > span {
            display: none;
          }
        }
      }
    }

    .articles-feed {
      padding: 20px;
    }
  }
}
</style>
